import React, { useEffect } from 'react';
import { useStore } from '../store';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section style={{ marginBottom: '2.5rem' }}>
    <h2 style={{
      fontFamily: 'var(--font-display)',
      fontSize: '1.05rem',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'var(--cyan)',
      marginBottom: '0.85rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.6rem',
    }}>
      <span style={{
        display: 'inline-block',
        width: 3,
        height: '1em',
        background: 'var(--cyan)',
        borderRadius: 2,
        boxShadow: '0 0 6px var(--cyan)',
        flexShrink: 0,
      }} />
      {title}
    </h2>
    <div style={{ fontSize: '0.88rem', color: 'var(--silver-mid)', lineHeight: 1.8 }}>
      {children}
    </div>
  </section>
);

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ marginBottom: '0.75rem' }}>{children}</p>
);

const Ul: React.FC<{ items: string[] }> = ({ items }) => (
  <ul style={{ paddingLeft: '1.25rem', marginBottom: '0.75rem' }}>
    {items.map((item, i) => <li key={i} style={{ marginBottom: '0.35rem' }}>{item}</li>)}
  </ul>
);

export const TermsPage: React.FC = () => {
  const { lang } = useStore();
  const fr = lang === 'fr';

  useEffect(() => {
    document.title = fr
      ? "Conditions d'utilisation — Modern Mercenaries"
      : 'Terms of Use — Modern Mercenaries';
  }, [fr]);

  return (
    <div className="page-container" style={{ paddingTop: '3rem', maxWidth: 800 }}>
      {/* Language toggle */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem' }}>
        <a href="?lang=en" style={{
          fontFamily: 'var(--font-mono)', fontSize: '0.7rem', letterSpacing: '0.1em',
          padding: '0.25rem 0.6rem',
          border: `1px solid ${!fr ? 'var(--cyan)' : 'var(--navy-border)'}`,
          borderRadius: 'var(--radius-sm)',
          color: !fr ? 'var(--cyan)' : 'var(--silver-dim)',
          background: !fr ? 'var(--cyan-deep)' : 'transparent',
          textDecoration: 'none',
        }}>EN</a>
        <a href="?lang=fr" style={{
          fontFamily: 'var(--font-mono)', fontSize: '0.7rem', letterSpacing: '0.1em',
          padding: '0.25rem 0.6rem',
          border: `1px solid ${fr ? 'var(--cyan)' : 'var(--navy-border)'}`,
          borderRadius: 'var(--radius-sm)',
          color: fr ? 'var(--cyan)' : 'var(--silver-dim)',
          background: fr ? 'var(--cyan-deep)' : 'transparent',
          textDecoration: 'none',
        }}>FR</a>
      </div>

      {/* Title */}
      <div style={{ marginBottom: '2.5rem' }}>
        <div style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '0.62rem',
          letterSpacing: '0.25em',
          textTransform: 'uppercase',
          color: 'var(--cyan-dim)',
          marginBottom: '0.6rem',
        }}>
          MM//01 — {fr ? 'Document légal' : 'Legal document'}
        </div>
        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontSize: '2.2rem',
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: 'var(--white)',
          lineHeight: 1.1,
        }}>
          {fr ? "Conditions d'Utilisation" : 'Terms of Use'}
        </h1>
        <p style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '0.7rem',
          color: 'var(--silver-dim)',
          marginTop: '0.6rem',
          letterSpacing: '0.06em',
        }}>
          {fr ? 'Dernière mise à jour : Janvier 2025' : 'Last updated: January 2025'}
        </p>
      </div>

      <div style={{
        background: 'var(--glass-bg)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-xl)',
        backdropFilter: 'var(--glass-blur)',
        padding: 'clamp(1.5rem, 4vw, 2.5rem)',
      }}>
        {fr ? (
          <>
            <Section title="1. Acceptation des conditions">
              <P>En accédant à Modern Mercenaries et en créant un compte, vous acceptez d'être lié par ces conditions d'utilisation. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser le service.</P>
            </Section>

            <Section title="2. Description du service">
              <P>Modern Mercenaries est une place de marché numérique permettant à des clients (« Employeurs ») de publier des missions et à des professionnels indépendants (« Mercenaires ») d'y postuler. Nous agissons en tant qu'intermédiaire et ne sommes pas partie aux contrats entre Employeurs et Mercenaires.</P>
            </Section>

            <Section title="3. Éligibilité">
              <Ul items={[
                'Vous devez avoir au moins 18 ans pour utiliser le service.',
                'Vous devez avoir la capacité juridique de conclure des contrats.',
                'Vous devez fournir des informations exactes et complètes lors de votre inscription.',
              ]} />
            </Section>

            <Section title="4. Comptes utilisateurs">
              <P>Vous êtes responsable de la confidentialité de votre mot de passe et de toutes les activités effectuées depuis votre compte. Vous vous engagez à :</P>
              <Ul items={[
                'Ne pas partager vos identifiants de connexion.',
                'Signaler immédiatement toute utilisation non autorisée de votre compte.',
                'Ne pas créer plusieurs comptes ou usurper l\'identité d\'autrui.',
                'Ne pas utiliser le service à des fins illégales ou frauduleuses.',
              ]} />
            </Section>

            <Section title="5. Missions et paiements">
              <P><strong style={{ color: 'var(--white)' }}>Publication de missions :</strong> Les Employeurs s'engagent à publier uniquement des missions légales et légitimes, avec une description honnête et un budget réaliste.</P>
              <P><strong style={{ color: 'var(--white)' }}>Frais de plateforme :</strong> Modern Mercenaries prélève une commission de 7% sur le montant de chaque mission. Ces frais sont non remboursables une fois la mission démarrée.</P>
              <P><strong style={{ color: 'var(--white)' }}>Structure de paiement :</strong></P>
              <Ul items={[
                '10% versé au Mercenaire à la sélection (engagement).',
                '40% versé au Mercenaire au démarrage de la mission.',
                '50% versé au Mercenaire à la validation finale.',
              ]} />
            </Section>

            <Section title="6. Comportement interdit">
              <Ul items={[
                'Publier des missions illégales, discriminatoires ou contraires à l\'ordre public.',
                'Harceler, menacer ou intimider d\'autres utilisateurs.',
                'Contourner la plateforme pour effectuer des paiements directs.',
                'Publier des faux avis ou manipuler le système de notation.',
                'Utiliser des robots, scrapers ou automatismes non autorisés.',
                'Tenter de compromettre la sécurité du service.',
              ]} />
            </Section>

            <Section title="7. Contenu utilisateur">
              <P>Vous conservez la propriété du contenu que vous publiez. En publiant du contenu, vous accordez à Modern Mercenaries une licence non exclusive pour l'afficher et le transmettre aux fins du service. Vous garantissez que votre contenu ne viole pas les droits de tiers.</P>
            </Section>

            <Section title="8. Résiliation">
              <P>Nous nous réservons le droit de suspendre ou supprimer tout compte qui viole ces conditions, sans préavis ni remboursement. Vous pouvez supprimer votre compte à tout moment depuis vos paramètres de profil.</P>
            </Section>

            <Section title="9. Limitation de responsabilité">
              <P>Modern Mercenaries agit en tant qu'intermédiaire technique. Nous ne sommes pas responsables :</P>
              <Ul items={[
                'De la qualité du travail fourni par les Mercenaires.',
                'Des litiges entre Employeurs et Mercenaires.',
                'Des pertes indirectes, consécutives ou spéciales.',
                'Des interruptions de service dues à des facteurs hors de notre contrôle.',
              ]} />
              <P>Notre responsabilité totale est limitée aux montants que vous avez payés à Modern Mercenaries au cours des 12 derniers mois.</P>
            </Section>

            <Section title="10. Droit applicable">
              <P>Ces conditions sont régies par le droit français. Tout litige sera soumis à la juridiction des tribunaux compétents de Paris, France.</P>
            </Section>

            <Section title="11. Modifications">
              <P>Nous pouvons modifier ces conditions à tout moment. Les modifications importantes seront notifiées par email et/ou par une notice sur le site. L'utilisation continue du service après notification constitue une acceptation des nouvelles conditions.</P>
            </Section>

            <Section title="12. Contact">
              <P>Pour toute question : <strong style={{ color: 'var(--cyan)' }}>legal@modernmercenaries.io</strong></P>
            </Section>
          </>
        ) : (
          <>
            <Section title="1. Acceptance of Terms">
              <P>By accessing Modern Mercenaries and creating an account, you agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use the service.</P>
            </Section>

            <Section title="2. Description of Service">
              <P>Modern Mercenaries is a digital marketplace enabling clients ("Employers") to post missions and independent professionals ("Mercenaries") to apply for them. We act as an intermediary and are not a party to contracts between Employers and Mercenaries.</P>
            </Section>

            <Section title="3. Eligibility">
              <Ul items={[
                'You must be at least 18 years old to use the service.',
                'You must have the legal capacity to enter into contracts.',
                'You must provide accurate and complete information when registering.',
              ]} />
            </Section>

            <Section title="4. User Accounts">
              <P>You are responsible for the confidentiality of your password and all activities carried out from your account. You agree to:</P>
              <Ul items={[
                'Not share your login credentials.',
                'Immediately report any unauthorized use of your account.',
                'Not create multiple accounts or impersonate others.',
                'Not use the service for illegal or fraudulent purposes.',
              ]} />
            </Section>

            <Section title="5. Missions and Payments">
              <P><strong style={{ color: 'var(--white)' }}>Posting missions:</strong> Employers commit to posting only legal and legitimate missions, with an honest description and realistic budget.</P>
              <P><strong style={{ color: 'var(--white)' }}>Platform fee:</strong> Modern Mercenaries charges a 7% commission on each mission amount. These fees are non-refundable once a mission has started.</P>
              <P><strong style={{ color: 'var(--white)' }}>Payment structure:</strong></P>
              <Ul items={[
                '10% paid to the Mercenary upon selection (engagement).',
                '40% paid to the Mercenary upon mission start.',
                '50% paid to the Mercenary upon final completion.',
              ]} />
            </Section>

            <Section title="6. Prohibited Conduct">
              <Ul items={[
                'Posting illegal, discriminatory, or unlawful missions.',
                'Harassing, threatening, or intimidating other users.',
                'Bypassing the platform to make direct payments.',
                'Posting fake reviews or manipulating the rating system.',
                'Using unauthorized bots, scrapers, or automation.',
                'Attempting to compromise the security of the service.',
              ]} />
            </Section>

            <Section title="7. User Content">
              <P>You retain ownership of the content you post. By posting content, you grant Modern Mercenaries a non-exclusive license to display and transmit it for service purposes. You warrant that your content does not infringe third-party rights.</P>
            </Section>

            <Section title="8. Termination">
              <P>We reserve the right to suspend or delete any account that violates these terms, without notice or refund. You may delete your account at any time from your profile settings.</P>
            </Section>

            <Section title="9. Limitation of Liability">
              <P>Modern Mercenaries acts as a technical intermediary. We are not responsible for:</P>
              <Ul items={[
                'The quality of work provided by Mercenaries.',
                'Disputes between Employers and Mercenaries.',
                'Indirect, consequential, or special losses.',
                'Service interruptions due to factors beyond our control.',
              ]} />
              <P>Our total liability is limited to amounts you have paid to Modern Mercenaries in the preceding 12 months.</P>
            </Section>

            <Section title="10. Governing Law">
              <P>These terms are governed by French law. Any dispute shall be submitted to the jurisdiction of the competent courts of Paris, France.</P>
            </Section>

            <Section title="11. Modifications">
              <P>We may modify these terms at any time. Material changes will be notified by email and/or a site notice. Continued use of the service after notification constitutes acceptance of the new terms.</P>
            </Section>

            <Section title="12. Contact">
              <P>For any questions: <strong style={{ color: 'var(--cyan)' }}>legal@modernmercenaries.io</strong></P>
            </Section>
          </>
        )}
      </div>
    </div>
  );
};
